import { abschnitt, ersetze, h, knopf, meldung, schritte, setzeMeldung } from '../dom.js';
import { ergebnisVon, melde } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';
import { faehigkeiten, passkeyBereich } from './passkey.js';

interface Bluetooth {
  getAvailability(): Promise<boolean>;
}

/** Windows Hello (oder Touch ID): eingerichtete Geräte-Entsperrung für Passkeys. */
async function pruefeWindowsHello(): Promise<void> {
  if (typeof PublicKeyCredential === 'undefined') {
    await melde('windows-hello', 'nicht_unterstuetzt', 'Der Browser kann keine Passkeys.');
    return;
  }
  const vorhanden = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  const status = vorhanden ? 'ok' : 'fehler';
  if (ergebnisVon('windows-hello')?.status === status) return;
  await melde(
    'windows-hello',
    status,
    vorhanden ? 'Windows Hello (oder Touch ID) ist eingerichtet und für Passkeys nutzbar.' : 'Keine Geräte-Entsperrung eingerichtet (Windows Hello fehlt oder ist aus).',
    { plattformAuthenticator: vorhanden },
  );
}

async function pruefeBluetooth(): Promise<'ja' | 'nein' | 'unbekannt'> {
  const bluetooth = (navigator as Navigator & { bluetooth?: Bluetooth }).bluetooth;
  if (!bluetooth?.getAvailability) return 'unbekannt';
  const da = await bluetooth.getAvailability().catch(() => false);
  const status = da ? 'ok' : 'fehler';
  if (ergebnisVon('bluetooth')?.status !== status) {
    await melde('bluetooth', status, da ? 'Bluetooth ist vorhanden und eingeschaltet.' : 'Kein Bluetooth oder ausgeschaltet. Der Passkey vom Handy geht dann nicht.', {
      quelle: 'navigator.bluetooth.getAvailability()',
    });
  }
  return da ? 'ja' : 'nein';
}

export const pc: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  await pruefeWindowsHello();
  const bluetooth = await pruefeBluetooth();
  const statusKonto = meldung();
  const kontoBereich = h('div', {});

  const bluetoothVonHand = h(
    'div',
    { class: 'frage', role: 'group', 'aria-labelledby': 'frage-bluetooth' },
    h('p', { id: 'frage-bluetooth' }, 'Dieser Browser kann Bluetooth nicht selbst prüfen (am besten Chrome oder Edge verwenden). Hat der PC Bluetooth?'),
    knopf('Ja', () => melde('bluetooth', 'ok', 'Bluetooth vorhanden (laut Angabe).', { quelle: 'von Hand' })),
    knopf('Nein', () => melde('bluetooth', 'fehler', 'Kein Bluetooth (laut Angabe).', { quelle: 'von Hand' }), 'sekundaer'),
  );

  const zeigeKontoFrage = () =>
    ersetze(
      kontoBereich,
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-konto' },
        h('p', { id: 'frage-konto' }, 'Melden sich mehrere Personen mit demselben Windows-Konto an diesem PC an?'),
        knopf('Nein, jede Person hat ein eigenes Konto', async () => {
          await melde('gemeinsames-konto', 'ok', 'Kein gemeinsames Windows-Konto an diesem PC.', { gemeinsam: false });
          setzeMeldung(statusKonto, 'Gespeichert.', 'ok');
        }),
        knopf('Ja, gemeinsames Konto', () => zeigeKontoTest(), 'sekundaer'),
      ),
    );

  const zeigeKontoTest = () =>
    ersetze(
      kontoBereich,
      schritte(
        'Person A legt oben unter „Passkey“ einen Passkey mit ihrem Namen an (Windows Hello).',
        'Person B tippt danach auf „Mit Passkey anmelden“.',
        'Beobachten: Bietet Windows den Passkey von Person A an, und lässt er sich mit der PIN des Kontos nutzen?',
      ),
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-konto-test' },
        h('p', { id: 'frage-konto-test' }, 'Konnte Person B den Passkey von Person A nutzen?'),
        knopf('Ja, Passkey von A nutzbar', async () => {
          await melde('gemeinsames-konto', 'fehler', 'Gemeinsames Windows-Konto: Passkeys der anderen sind nutzbar. Ausweg: eigenes Konto je Person, USB-Sicherheitsschlüssel oder Ersatzweg.', {
            gemeinsam: true,
            fremderPasskeyNutzbar: true,
          });
          setzeMeldung(statusKonto, 'Gespeichert.', 'ok');
        }),
        knopf('Nein', async () => {
          await melde('gemeinsames-konto', 'ok', 'Gemeinsames Windows-Konto, aber der Passkey der anderen Person war nicht nutzbar.', { gemeinsam: true, fremderPasskeyNutzbar: false });
          setzeMeldung(statusKonto, 'Gespeichert.', 'ok');
        }, 'sekundaer'),
      ),
    );
  zeigeKontoFrage();

  ziel.append(
    ...kopf('Büro-PC', 'Windows Hello, Bluetooth für den Passkey vom Handy und gemeinsame Windows-Konten (Konzept, Anmeldung und Schutz vor Bots).'),
    abschnitt('Windows Hello', pruefpunktStand('windows-hello', aufraeumer)),
    abschnitt('Bluetooth', pruefpunktStand('bluetooth', aufraeumer), bluetooth === 'unbekannt' && bluetoothVonHand),
    abschnitt('Passkey am PC', await passkeyBereich(aufraeumer, 'anlegen-und-anmelden')),
    abschnitt(
      'Passkey vom Handy',
      schritte(
        'Auf dem Handy vorher einen Passkey anlegen (Prüfpunkt „Passkey“ in der App).',
        'Hier „Mit Passkey vom Handy anmelden“ wählen.',
        'Im Dialog „iPhone, iPad oder Android-Gerät“ bzw. „Anderes Gerät verwenden“ wählen und den QR-Code mit dem Handy scannen. Dafür braucht der PC Bluetooth.',
      ),
      await passkeyBereich(aufraeumer, 'vom-handy'),
    ),
    abschnitt('Gemeinsames Windows-Konto', pruefpunktStand('gemeinsames-konto', aufraeumer), kontoBereich, statusKonto),
    abschnitt('Was der Browser meldet', await faehigkeiten()),
  );
  return aufraeumer.alles;
};
